import { memo, useMemo } from 'react'
import { Divider } from '@cargomart/ui'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { getFormattedPriceWithRouble } from 'helpers/string'
import { TerminalItem } from 'src/schema'
import { OrderOperationItem } from 'src/schema/models/order_operation_item'

import { OrderCreateOperationForm } from '../OrderCreatePointOperation/types'
import { OperationGroup } from './OperationGroup'
import { OrderOperationItemSummary } from './types'

type TProps = {
  departureTerminal: TerminalItem | null
  destinationTerminal: TerminalItem | null
  operations: OrderOperationItem[]
  className?: string
  isCargoAccepted?: boolean
  isLoading?: boolean
  orderOperation?: OrderCreateOperationForm[]
  totalPrice?: string
}

export const OrderOperationsBlock = memo<TProps>(function OrderOperationsBlock(props) {
  const {
    className,
    departureTerminal,
    totalPrice,
    destinationTerminal,
    operations,
    orderOperation = [],
    isCargoAccepted = false,
    isLoading
  } = props

  const allOperations = useMemo(() => {
    const transformedOperations: OrderOperationItemSummary[] = operations.map((op) => ({
      ...op,
      source: 'delivery'
    }))

    const deliveryKeys = new Set(operations.map((op) => `${op.operation.id}:${op.terminalId ?? ''}`))

    const transformedOrderOperations: OrderOperationItemSummary[] = orderOperation
      .filter((op) => !deliveryKeys.has(`${op.operationId}:${op.terminalId ?? ''}`))
      .map((op) => ({
        amount: op.amount ?? 1,
        operation: {
          id: op.operationId,
          name: op.name
        },
        total: 0,
        terminalId: op.terminalId,
        source: 'order'
      }))

    return [...transformedOperations, ...transformedOrderOperations]
  }, [operations, orderOperation])

  const operationByTerminals = useMemo(
    () =>
      allOperations.reduce<{
        base: OrderOperationItemSummary[]
        departure: OrderOperationItemSummary[]
        destination: OrderOperationItemSummary[]
      }>(
        (acc, operation) => {
          if (operation.terminalId === departureTerminal?.id) {
            acc.departure.push(operation)

            return acc
          }

          if (operation.terminalId === destinationTerminal?.id) {
            acc.destination.push(operation)

            return acc
          }

          acc.base.push({ ...operation, terminalId: undefined })

          return acc
        },
        {
          base: [],
          destination: [],
          departure: []
        }
      ),
    [departureTerminal?.id, destinationTerminal?.id, allOperations]
  )

  return (
    <div className={className} data-test-id="order--cost--block">
      <h2 className="pb-4 text-headline-sm text-on-surface">Расчёт стоимости</h2>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col items-start gap-4 self-stretch text-body-md text-on-surface">
          {!!operationByTerminals.base.length && (
            <>
              <OperationGroup
                hasTooltip={!isCargoAccepted}
                isLoading={isLoading}
                operations={operationByTerminals.base}
              />

              <Divider className="border border-dashed border-outline bg-transparent" />
            </>
          )}

          {!!operationByTerminals.departure.length && (
            <>
              <OperationGroup
                hasTooltip={!isCargoAccepted}
                isLoading={isLoading}
                operations={operationByTerminals.departure}
                terminalName={departureTerminal?.name}
              />
              <Divider className="border border-dashed border-outline bg-transparent" />
            </>
          )}

          {!!operationByTerminals.destination.length && (
            <>
              <OperationGroup
                hasTooltip={!isCargoAccepted}
                isLoading={isLoading}
                operations={operationByTerminals.destination}
                terminalName={destinationTerminal?.name}
              />
              <Divider />
            </>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2 self-stretch text-body-md text-on-surface">
            {!isLoading ? (
              <>
                <div className="flex-1">Общая стоимость</div>
                <div className="text-right">{getFormattedPriceWithRouble(totalPrice)}</div>
              </>
            ) : (
              <PlaceholderCell height={21} />
            )}
          </div>
          <div className="text-body-sm text-on-surface-variant">
            Стоимость расчётная, точная сумма определяется после замера и&nbsp;оформления груза
          </div>
        </div>
      </div>
    </div>
  )
})
